import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";
import {
  STORY_MODE_CHAPTER_COUNT,
  STORY_MODE_MISSIONS_PER_CHAPTER,
  STORY_MODE_TOTAL_MISSIONS,
} from "@/constants/story";
import {
  claimStoryModeReward,
  getStoryModeUserMissionStatus,
} from "@/helper/contractFunctions/MafiaStoryMode";
import type { StoryModeLeaderboardEntry } from "@/types/StoryMode/StoryModeLeaderboardEntry";

export interface StoryModeMissionStatus {
  currentStage: number;
  isMissionCompleted: boolean[];
  isStarted: boolean;
  startedAt: number;
  completedAt: number[];
}

export interface StoryState {
  isLoadingStoryModeMissionStatus: boolean;
  storyModeMissionStatus: StoryModeMissionStatus;
  isClaimingStoryModeReward: boolean;
  storyModeProgress: number;
  isLoadingStoryModeLeaderboard: boolean;
  storyModeLeaderboard: StoryModeLeaderboardEntry[];
}

const initialState: StoryState = {
  isLoadingStoryModeMissionStatus: false,
  storyModeMissionStatus: {
    currentStage: 0,
    isMissionCompleted: [],
    isStarted: false,
    startedAt: 0,
    completedAt: [],
  },
  isClaimingStoryModeReward: false,
  storyModeProgress: 0,
  isLoadingStoryModeLeaderboard: false,
  storyModeLeaderboard: [],
};

export const getStoryModeUserMissionStatusAction = createAsyncThunk(
  "story/getStoryModeUserMissionStatusAction",
  getStoryModeUserMissionStatus
);

export const claimStoryModeRewardAction = createAsyncThunk(
  "story/claimStoryModeRewardAction",
  claimStoryModeReward
);

export const getStoryModeLeaderboardAction = createAsyncThunk(
  "story/getStoryModeLeaderboardAction",
  async () => {
    const { data } = await axios.get(`${SERVER_URL}/story-mode/leaderboard`);
    return data;
  }
);

const storySlice = createSlice({
  name: "story",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStoryModeUserMissionStatusAction.pending, (state) => {
        state.isLoadingStoryModeMissionStatus = true;
      })
      .addCase(
        getStoryModeUserMissionStatusAction.fulfilled,
        (state, { payload }) => {
          state.isLoadingStoryModeMissionStatus = false;
          state.storyModeMissionStatus = payload;

          const completedMissions =
            payload.currentStage >= STORY_MODE_CHAPTER_COUNT
              ? STORY_MODE_TOTAL_MISSIONS
              : payload.currentStage * STORY_MODE_MISSIONS_PER_CHAPTER +
                (payload.isMissionCompleted?.filter(Boolean).length ?? 0);

          state.storyModeProgress = Math.min(
            parseFloat(
              ((completedMissions * 100) / STORY_MODE_TOTAL_MISSIONS).toFixed(2)
            ),
            100
          );
        }
      )
      .addCase(getStoryModeUserMissionStatusAction.rejected, (state, { error }) => {
        state.isLoadingStoryModeMissionStatus = false;
        console.log(error.message);
      })
      .addCase(claimStoryModeRewardAction.pending, (state) => {
        state.isClaimingStoryModeReward = true;
      })
      .addCase(claimStoryModeRewardAction.fulfilled, (state) => {
        state.isClaimingStoryModeReward = false;
      })
      .addCase(claimStoryModeRewardAction.rejected, (state, { error }) => {
        state.isClaimingStoryModeReward = false;
        console.log(error.message);
      })
      .addCase(getStoryModeLeaderboardAction.pending, (state) => {
        state.isLoadingStoryModeLeaderboard = true;
      })
      .addCase(getStoryModeLeaderboardAction.fulfilled, (state, { payload }) => {
        state.isLoadingStoryModeLeaderboard = false;
        state.storyModeLeaderboard = payload.data;
      })
      .addCase(getStoryModeLeaderboardAction.rejected, (state, { error }) => {
        state.isLoadingStoryModeLeaderboard = false;
        console.log(error.message);
      });
  },
});

export default storySlice.reducer;
